/**
 * The single axios instance, built from the vendored fleet factory. Two surfaces, strictly
 * separated:
 *  - saas-auth COOKIE surface (`/api/auth/**`): base = getBootstrap().authOrigin (verbatim);
 *    withCredentials; CSRF cookie-echo on mutating calls; NO bearer.
 *  - cnp-api BEARER surface (everything else, e.g. `/me`): base = env.apiBaseUrl (this origin +
 *    `/api`); Authorization: Bearer; NO credentials; NO CSRF.
 * A 401 on the bearer surface triggers ONE silent refresh and ONE retry (the factory's `_retry`
 * guard). Auth-surface requests are pre-marked `_retry` so they are never refreshed.
 * Bootstrap does NOT use this client (src/config/bootstrap.ts uses fetch).
 */
import { type AxiosInstance } from 'axios';
import { createApiClient } from './httpClientFactory';
import { env } from '../config/env';
import { getBootstrap } from '../config/bootstrap';
import {
  getAccessToken,
  getCsrfToken,
  notifySessionExpired,
  setAccessToken,
  setCsrfToken,
} from '../auth/authStore';
import { generateRequestId } from '../utils/requestId';

export function isAuthSurface(path: string): boolean {
  return path.startsWith('/api/auth/');
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const apiClient: AxiosInstance = createApiClient({
  resolveBaseUrl: (path) => (isAuthSurface(path) ? getBootstrap().authOrigin : env.apiBaseUrl),
  sendCredentials: isAuthSurface,
  tokens: {
    getAccess: getAccessToken,
    setAccess: (token) => setAccessToken(token),
    getCsrf: getCsrfToken,
    setCsrf: setCsrfToken,
  },
  bearer: { protected: (path) => !isAuthSurface(path) },
  csrf: {
    protected: (path, method) => isAuthSurface(path) && MUTATING.has(method),
    mode: () => 'cookie-echo',
  },
  refresh: { path: '/api/auth/refresh' },
  onSessionExpired: notifySessionExpired,
  generateRequestId,
});

// Scope the silent refresh to the bearer surface: pre-mark every auth-surface request `_retry`
// so the factory's `!_retry` gate skips it — a 401 from refresh / logout / csrf is normalized,
// never refreshed. (axios runs request interceptors in reverse registration order, so this runs
// before the factory's; it only sets a flag the response interceptor reads.)
apiClient.interceptors.request.use((req) => {
  const r = req as { _retry?: boolean };
  if (isAuthSurface(pathOf(req)) && r._retry === undefined) r._retry = true;
  return req;
});

function pathOf(req: { url?: string }): string {
  const u = req.url ?? '';
  if (u.startsWith('http')) {
    try {
      return new URL(u).pathname;
    } catch {
      return u;
    }
  }
  return u;
}
